
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';

export default function Hero() {
  const { t } = useTranslation('common');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src="https://public.readdy.ai/ai/img_res/4b30d918ee3ad54ea4364e527c806919.jpg"
          alt="Modern Architecture"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/40 via-blue-800/15 to-blue-900/50"></div>
      </div>

      {/* Floating geometric accent */}
      <div className="absolute top-20 right-20 w-72 h-72 border border-blue-200/10 rounded-full opacity-30 animate-pulse"></div>
      <div className="absolute bottom-32 left-16 w-48 h-48 border border-blue-200/10 rounded-full opacity-20"></div>

      {/* Content */}
      <div className={`relative z-10 w-full text-center px-6 max-w-5xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        {/* Top Badge */}
        <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 px-6 py-2.5 rounded-full mb-10">
          <div className="w-2 h-2 rounded-full bg-blue-300 animate-pulse"></div>
          <span className="text-white/90 text-xs font-medium tracking-widest uppercase">{t('hero.badge')}</span>
        </div>

        {/* Main Title */}
        <h1 className="text-white font-light mb-8 tracking-tight" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
          <div className="text-5xl md:text-6xl lg:text-7xl mb-3 font-extralight">{t('hero.title1')}</div>
          <div className="text-5xl md:text-6xl lg:text-7xl font-semibold">{t('hero.title2')}</div>
        </h1>

        {/* Subtitle */}
        <p className="text-white/70 text-base md:text-lg mb-12 leading-relaxed max-w-2xl mx-auto font-light">
          {t('hero.subtitle')}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a 
            href="#contact" 
            className="group bg-blue-50 text-blue-900 px-10 py-4 rounded-full text-sm font-medium hover:bg-white transition-all duration-300 shadow-2xl hover:shadow-blue-100/20 whitespace-nowrap cursor-pointer flex items-center gap-3"
          >
            {t('hero.cta1')}
            <i className="ri-arrow-right-line text-base group-hover:translate-x-1 transition-transform"></i>
          </a>
          <a 
            href="#products" 
            className="text-white px-10 py-4 rounded-full text-sm font-medium hover:bg-white/10 transition-all duration-300 border border-white/30 backdrop-blur-sm whitespace-nowrap cursor-pointer"
          >
            {t('hero.cta2')}
          </a>
        </div>
      </div>

      {/* Bottom Stats Bar */}
      <div className={`absolute bottom-0 left-0 right-0 z-10 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="max-w-5xl mx-auto px-6 pb-12">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl px-8 py-6 grid grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-light text-white mb-1">15<span className="text-blue-300">+</span></div>
              <div className="text-white/50 text-xs tracking-wider uppercase">{t('about.stat1')}</div>
            </div>
            <div className="text-center border-x border-white/10">
              <div className="text-3xl md:text-4xl font-light text-white mb-1">1000<span className="text-blue-300">+</span></div>
              <div className="text-white/50 text-xs tracking-wider uppercase">{t('about.stat2')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-light text-white mb-1">98<span className="text-blue-300">%</span></div>
              <div className="text-white/50 text-xs tracking-wider uppercase">{t('about.stat3')}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
